import React, { useMemo, useRef, useState } from 'react';
import useProductsList from '@stone/hooks/useProductsList';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Button from '@stone/ui/button/Button';
import { useTranslation } from 'react-i18next';
import Icon from '@stone/ui/icon/Icon';
import classNames from 'classnames';
import Modal from 'react-modal';
import ProductTilesListcontext from '@stone/scripts/contexts/ProductTilesListContext';
import ProductTileSimple from '../product/ProductTileSimple';
import customEventBus from '@common/scripts/libs/eventBus';
import SoundWave from './SoundWave';
import ModalDialog from '../modal/ModalDialog';
import EditorialVideoContainer from '../editorial/EditorialVideoContainer';
import useBreakpoint from '@common/utils/useBreakpoint';
import { useLangChecker } from '@common/utils/i18n';
gsap.registerPlugin(ScrollTrigger);
const FlpHeroStickyBar = (props) => {
    // ANCHOR - HOOKS --------------------------------------------------------------------
    const listName = `flp/${props.itemListName || props.title.toLowerCase().split(' ').join('-')}`;
    const { parseCategory, getProducts, products } = useProductsList(listName);
    const { t } = useTranslation();
    // ANCHOR - COSTANTS --------------------------------------------------------------------
    const category = parseCategory(props);
    const currentBreakpoint = useBreakpoint();
    const listContext = useMemo(() => {
        let context = {};
        context.analyticsData = {
            item_list_name: listName,
        };
        return context;
    }, [props.itemListName]);
    // ANCHOR - USE REF -------------------------------------------------------------------
    const fullRef = useRef();
    const simpleRef = useRef();
    // ANCHOR - USE MEMO --------------------------------------------------------------------
    const isIndexOpen = useMemo(() => props.dialogOpen === 'INDEX', [props.dialogOpen]);
    const isFilmOpen = useMemo(() => props.dialogOpen === 'FILM', [props.dialogOpen]);
    const hasIndex = useMemo(() => props.category || props.productsMultiline, [props]);
    const hasAudio = useMemo(() => !!props.audioId, [props]);
    const hasFilm = useMemo(() => !!props.filmThronId, [props]);
    // @ts-ignore
    const isDesktop = useMemo(() => currentBreakpoint >= window.mqObj.lg, [currentBreakpoint]);
    // ANCHOR - USE STATE --------------------------------------------------------------------
    const [progress, setProgress] = useState(0);
    const [isSticky, setIsSticky] = useState(false);
    const SCROLL_IDS = ['flp-progress', 'flp-color', 'flp-sticky-desktop', 'flp-sticky-mobile'];
    // SECTION - GSAP --------------------------------------------------------------------
    useGSAP(() => {
        SCROLL_IDS.forEach((id) => { var _a; return (_a = ScrollTrigger.getById(id)) === null || _a === void 0 ? void 0 : _a.kill(true); });
        const contextualRef = isDesktop ? fullRef : simpleRef;
        const height = contextualRef.current.getBoundingClientRect().height;
        const tlProgress = gsap.to('.flp-hero-subnav__progress--scroller', {
            width: '100%',
            ease: 'none',
            // onStart: () => {
            //   refreshScrollTriggers.forEach((id) => ScrollTrigger.getById(id).refresh());
            // },
        });
        const tlTransparence = gsap.to(contextualRef.current, {
            filter: 'invert(100%)',
            backgroundColor: 'rgba(0,0,0,1)',
        });
        ScrollTrigger.create({
            id: 'flp-progress',
            trigger: '#flp-hero-progress-trigger',
            markers: props.showMarkers,
            start: 'top top',
            end: 'top bottom',
            endTrigger: '#end-scroll-progress',
            scrub: true,
            animation: tlProgress,
            onUpdate: ({ progress }) => setProgress(progress),
            onEnter: () => {
                setIsSticky(true);
                customEventBus.on('header:onprogress', (data) => {
                    gsap.set(contextualRef.current, { y: -(data.scroll + data.height) });
                });
            },
            onLeaveBack: () => {
                setIsSticky(false);
                customEventBus.off('header:onprogress', (data) => {
                    gsap.set(contextualRef.current, { y: -(data.scroll + data.height) });
                });
            },
        });
        ScrollTrigger.create({
            id: 'flp-color',
            trigger: '.flp-hero',
            // markers: props.showMarkers,
            start: () => `bottom top+=${height}`,
            end: () => `+=${height}`,
            scrub: true,
            animation: tlTransparence,
        });
        const tlOpacity = gsap.timeline();
        if (!isDesktop) {
            tlOpacity.to(simpleRef.current, { opacity: 1 }).to(['.flp-hero__title--name', '.flp-hero__title--season'], { opacity: 0 }, '<');
            ScrollTrigger.create({
                id: 'flp-sticky-mobile',
                trigger: fullRef.current,
                // markers: props.showMarkers,
                start: () => `top top+=${height}`,
                scrub: true,
                end: () => `+=44`,
                animation: tlOpacity,
            });
        }
        else {
            ScrollTrigger.create({
                id: 'flp-sticky-desktop',
                trigger: fullRef.current,
                pin: true,
                pinSpacing: false,
                // markers: props.showMarkers,
                start: 'top top',
                endTrigger: 'footer',
            });
        }
    }, { scope: fullRef, dependencies: [isDesktop] });
    // !SECTION - GSAP
    // ANCHOR - COMPONENTS --------------------------------------------------------------------
    const renderCollectionLink = (full) => {
        if (!props.linkUrl || !props.linkLabel) {
            return null;
        }
        return (React.createElement("a", { href: props.linkUrl, "aria-label": props === null || props === void 0 ? void 0 : props.linkLabelA11n, className: "button button--tertiary inverted", tabIndex: !full ? -1 : undefined },
            React.createElement("span", null, props.linkLabel)));
    };
    const renderWatchLink = () => (React.createElement(Button, { type: "button", cls: "button--tertiary", inverted: true, onClick: () => props.setDialogOpen('FILM'), "aria-haspopup": "dialog" }, t('flp.button.watchfilm')));
    const renderIndexButton = () => {
        return (React.createElement("button", { type: "button", className: "button button--quaternary inverted", onClick: () => (isIndexOpen ? props.setDialogOpen(null) : props.setDialogOpen('INDEX')), "aria-label": isIndexOpen ? t('button.close') : t('flp.button.openindex'), "aria-haspopup": !isIndexOpen ? 'dialog' : undefined },
            React.createElement(Icon, { name: "open-white", family: "10" })));
    };
    const renderAudio = () => {
        var _a, _b;
        return (React.createElement(React.Fragment, null,
            React.createElement(Button, { className: "audiotrack__vawe-play", cls: ['button--quaternary'], type: "button", inverted: true, onClick: () => {
                    const id = `${props.id}-play-audio`;
                    const button = document.getElementById(id);
                    button === null || button === void 0 ? void 0 : button.click();
                } },
                React.createElement(React.Fragment, null,
                    React.createElement(Icon, { name: props.isAudioPlaying ? 'speaker-on-white' : 'speaker-off-white', family: "16" }),
                    (props.isAudioPlaying && t('button.pauseshort')) || React.createElement("span", { ref: useLangChecker }, t('button.playshort')))),
            React.createElement(SoundWave, { isPlaying: props.isAudioPlaying, progress: ((_a = props.audioStatus) === null || _a === void 0 ? void 0 : _a.currentTime) / ((_b = props.audioStatus) === null || _b === void 0 ? void 0 : _b.duration) })));
    };
    // ANCHOR - STICKY BAR --------------------------------------------------------------------
    const renderFullStickyBar = (isModal) => (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames('flp-hero-subnav', { 'visually-hidden': !isModal && isIndexOpen }, { 'disable-anim': !isSticky }), ref: fullRef },
            isDesktop && (React.createElement("div", { className: "flp-hero-subnav__progress" },
                React.createElement("div", { className: "flp-hero-subnav__progress--scroller", style: isModal && { width: `${progress * 100}%` } }))),
            React.createElement("div", { className: "container" },
                React.createElement("div", { className: "row" },
                    React.createElement("div", { className: props.type === 'FLP' ? 'col-lg-2' : 'col-lg-3 col-xxl-2' },
                        React.createElement("p", { className: "font__title flp-hero__title--season" }, props.season)),
                    React.createElement("div", { className: "col-lg-4 col-xxl-3" },
                        React.createElement("p", { className: "font__title flp-hero__title--name" }, props.title)),
                    React.createElement("div", { className: props.type === 'FLP' ? 'col-lg-10 col-xxl-12' : 'col-lg-9 col-xxl-12' }, props.subtitle && React.createElement("p", { className: "font__title flp-hero__title--subtitle" }, props.subtitle)),
                    React.createElement("div", { className: `col-lg-${props.type === 'FLP' ? 6 : 5} col-xxl-5 flp-hero-subnav__ctas ${props.type === 'ARCHIVE' ? 'archive' : ''}` },
                        props.filmThronId && renderWatchLink(),
                        renderCollectionLink(true),
                        !isDesktop && hasAudio && props.type === 'ARCHIVE' && React.createElement("div", { className: "flp-hero-subnav__audio" }, renderAudio())),
                    isDesktop && hasIndex && props.type === 'FLP' && React.createElement("div", { className: "col-fill flp-hero-subnav__index" }, renderIndexButton()),
                    isDesktop && hasAudio && props.type === 'ARCHIVE' && React.createElement("div", { className: "col-fill flp-hero-subnav__audio" }, renderAudio()))))));
    const renderSimpleStickyBar = () => (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames('flp-hero-subnav--simple', { 'd-none': isDesktop }), ref: simpleRef, "aria-hidden": "true" },
            React.createElement("div", { className: "flp-hero-subnav__progress" },
                React.createElement("div", { className: "flp-hero-subnav__progress--scroller" })),
            React.createElement("p", { className: "font__title" }, props.title),
            hasAudio && props.type === 'ARCHIVE' ? React.createElement("div", { className: "flp-hero-subnav__audio" }, renderAudio()) : renderCollectionLink(false))));
    // !SECTION - COMPONENTS
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { id: 'flp-hero-progress-trigger' }),
        renderSimpleStickyBar(),
        renderFullStickyBar(),
        hasIndex && (React.createElement(Modal, { isOpen: isIndexOpen, className: "flp-hero__modal flp-hero__modal--index", overlayClassName: "flp-hero__modal-overlay", onRequestClose: () => props.setDialogOpen(null), onAfterOpen: () => {
                if (!(products === null || products === void 0 ? void 0 : products.length)) {
                    getProducts(category);
                }
            }, contentLabel: props === null || props === void 0 ? void 0 : props.title },
            renderFullStickyBar(true),
            React.createElement("div", { className: "flp-hero__modal-body container" }, !!(products === null || products === void 0 ? void 0 : products.length) && (React.createElement(ProductTilesListcontext.Provider, { value: listContext },
                React.createElement("div", { role: "region" },
                    React.createElement("ul", { className: "grid-container container" }, products.map((el, i) => {
                        var _a;
                        return (React.createElement("li", { key: `${el.uuid}_${i}`, "aria-labelledby": `${el.uuid}_link` },
                            React.createElement(ProductTileSimple, { product: el, type: "RECS", customImage: (_a = el.assetOverride) === null || _a === void 0 ? void 0 : _a.car, hidePrice: true, hideSeoName: true })));
                    })))))))),
        hasFilm && (React.createElement(ModalDialog, { isOpen: isFilmOpen, className: "flp-hero__modal flp-hero__modal--film", overlayClassName: "flp-hero__modal-overlay", onRequestClose: () => props.setDialogOpen(null), contentLabel: t('flp.button.watchfilm'), fullHeight: true, fullWidth: true, noSkin: true }, isDesktop ? (React.createElement("div", { className: "container" },
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: "col-lg-20 offset-lg-2" },
                    React.createElement(EditorialVideoContainer, { thronId: props.filmThronId, lockBitrate: (props === null || props === void 0 ? void 0 : props.filmMaxBitrate) == 'true', forceActiveState: true }))))) : (React.createElement(EditorialVideoContainer, { thronId: props.filmThronId, lockBitrate: (props === null || props === void 0 ? void 0 : props.filmMaxBitrate) == 'true', forceActiveState: true }))))));
};
export default FlpHeroStickyBar;
